import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { skipToken, useQuery } from '@tanstack/react-query'
import { useAccount, usePublicClient } from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'

import {
  AuthKeeperABI,
  type KYCLevel,
  KYCLevels,
} from '../blockchain/abis/AuthKeeper'
import { KYCComplianceFacetAbi } from '../blockchain/abis/KYCComplianceFacet'
import {
  KYCComplianceModuleAbi,
  RestrictionTypes,
} from '../blockchain/abis/KYCComplianceModule'
import {
  disableKeyWhenMissing,
  type DisableKeyWhenMissingFlag,
  reportEnableFailed,
} from '../helpers/utilities'
import { type ProductSymbol, useGetTokenAddress } from '../products'

export const keys = createQueryKeyStore({
  token_auth: {
    is_restricted: (variables: {
      symbol: ProductSymbol
      wallet_address: CryptoAddress | DisableKeyWhenMissingFlag
    }) => [variables],
    kyc_compliance_address: (variables: { symbol: ProductSymbol }) => [
      variables,
    ],
    auth_keeper_address: null,
    kyc_level: (variables: {
      wallet_address: CryptoAddress | DisableKeyWhenMissingFlag
    }) => [variables],
  },
})

export function useGetKYCComplianceAddress({
  symbol,
}: {
  symbol: ProductSymbol
}) {
  const token_address = useGetTokenAddress({ symbol })
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = token_address != null && client != null

  reportEnableFailed({
    name: 'useGetKYCComplianceAddress',
    parts: {
      client,
      token_address,
    },
  })

  return useQuery({
    staleTime: Infinity,
    gcTime: Infinity,
    queryKey: keys.token_auth.kyc_compliance_address({ symbol }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await client.readContract({
            abi: KYCComplianceFacetAbi,
            address: token_address,
            functionName: 'getKYCModules',
          })
          return result == null ? result : result[0]
        },
  })
}

export function useGetAuthKeeperAddress() {
  const { data: kyc_compliance_address } = useGetKYCComplianceAddress({
    symbol: 'TECH',
  })
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = kyc_compliance_address != null && client != null

  reportEnableFailed({
    name: 'useGetAuthKeeperAddress',
    parts: {
      client,
      kyc_compliance_address,
    },
  })

  return useQuery({
    staleTime: Infinity,
    gcTime: Infinity,
    queryKey: keys.token_auth.auth_keeper_address.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () =>
          client.readContract({
            abi: KYCComplianceModuleAbi,
            address: kyc_compliance_address,
            functionName: 'getAuthKeeper',
          }),
  })
}

type is_restricted_data_type = {
  mint: boolean
  trade_fdex: boolean
  trade_other: boolean
}

export function useCheckIsRestricted({ symbol }: { symbol: ProductSymbol }) {
  const { data: kyc_compliance_address } = useGetKYCComplianceAddress({
    symbol,
  })
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    kyc_compliance_address != null && wallet_address != null && client != null

  reportEnableFailed({
    name: 'useCheckIsRestricted',
    parts: {
      client,
      wallet_address,
      kyc_compliance_address,
    },
  })

  return useQuery<is_restricted_data_type>({
    staleTime: Infinity,
    queryKey: keys.token_auth.is_restricted({
      symbol,
      wallet_address: disableKeyWhenMissing(wallet_address),
    }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const results = await client.multicall({
            contracts: [
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('Mint'), wallet_address],
              },
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('TradeFDEX'), wallet_address],
              },
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('TradeOther'), wallet_address],
              },
            ],
          })

          return {
            mint: results[0].result == true,
            trade_fdex: results[1].result == true,
            trade_other: results[2].result == true,
          }
        },
  })
}

export function useCheckKYCLevel() {
  const { data: auth_keeper_address } = useGetAuthKeeperAddress()
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    auth_keeper_address != null && wallet_address != null && client != null

  reportEnableFailed({
    name: 'useCheckKYCLevel',
    parts: {
      client,
      wallet_address,
      auth_keeper_address,
    },
  })

  return useQuery<KYCLevel>({
    staleTime: Infinity,
    queryKey: keys.token_auth.kyc_level({
      wallet_address: disableKeyWhenMissing(wallet_address),
    }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await client.readContract({
            abi: AuthKeeperABI,
            address: auth_keeper_address,
            functionName: 'getKYCLevel',
            args: [wallet_address],
          })

          return KYCLevels[result]
        },
  })
}
